import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Star, MessageSquare, User, Calendar, ThumbsUp, ThumbsDown, 
  TrendingUp, TrendingDown, AlertTriangle, Award, Send, Filter,
  ChevronDown, ChevronUp, Smile, Frown, Meh
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StarDisplay = ({ rating, size = 'sm' }) => {
  const sizes = { sm: 'w-4 h-4', md: 'w-5 h-5', lg: 'w-6 h-6' };
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(star => (
        <Star 
          key={star}
          className={`${sizes[size]} ${
            star <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'
          }`}
        />
      ))}
    </div>
  );
};

const SentimentBadge = ({ sentiment }) => {
  const config = {
    positive: { icon: Smile, color: 'bg-green-100 text-green-700 border-green-200', label: 'Positive' },
    negative: { icon: Frown, color: 'bg-red-100 text-red-700 border-red-200', label: 'Negative' },
    neutral: { icon: Meh, color: 'bg-gray-100 text-gray-700 border-gray-200', label: 'Neutral' }
  };
  const cfg = config[sentiment] || config.neutral;
  const Icon = cfg.icon;
  
  return (
    <Badge variant="outline" className={cfg.color}>
      <Icon className="w-3 h-3 mr-1" />
      {cfg.label}
    </Badge>
  );
};

const TherapistFeedback = ({ therapistId, therapistName }) => {
  const [feedbackReceived, setFeedbackReceived] = useState([]);
  const [customerFeedback, setCustomerFeedback] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('received');
  const [filterRating, setFilterRating] = useState('all');
  const [expandedFeedback, setExpandedFeedback] = useState(null);
  
  // Customer feedback dialog
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerRating, setCustomerRating] = useState({
    behavior: '',
    punctuality: '',
    communication: '',
    would_serve_again: '',
    notes: ''
  });
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [therapistId]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [receivedRes, givenRes, statsRes] = await Promise.all([
        axios.get(`${API}/therapist/${therapistId}/feedback/received`, { headers }),
        axios.get(`${API}/therapist/${therapistId}/feedback/given`, { headers }),
        axios.get(`${API}/therapist/${therapistId}/feedback/stats`, { headers })
      ]);
      
      setFeedbackReceived(receivedRes.data);
      setCustomerFeedback(givenRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching feedback:', error);
      // Set empty arrays if API fails (endpoints may not exist yet)
      setFeedbackReceived([]);
      setCustomerFeedback([]);
      setStats({
        total_feedback: 0,
        average_rating: 0,
        positive_count: 0,
        negative_count: 0,
        neutral_count: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const openFeedbackDialog = (service) => {
    setSelectedCustomer(service);
    setCustomerRating({
      behavior: '',
      punctuality: '',
      communication: '',
      would_serve_again: '',
      notes: ''
    });
    setFeedbackDialogOpen(true);
  };

  const submitCustomerFeedback = async () => {
    if (!customerRating.behavior || !customerRating.would_serve_again) {
      toast.error('Please fill required fields');
      return;
    }
    
    setSubmittingFeedback(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/therapist/feedback/customer`, {
        service_id: selectedCustomer.service_id,
        customer_phone: selectedCustomer.customer_phone,
        customer_name: selectedCustomer.customer_name,
        therapist_id: therapistId,
        ...customerRating
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Feedback submitted successfully');
      setFeedbackDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const filteredFeedback = feedbackReceived.filter(f => {
    if (filterRating === 'all') return true;
    if (filterRating === 'positive') return f.overall_experience >= 4;
    if (filterRating === 'negative') return f.overall_experience <= 2;
    return f.overall_experience === 3;
  });

  const getSentiment = (rating) => {
    if (rating >= 4) return 'positive';
    if (rating <= 2) return 'negative';
    return 'neutral';
  };

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Loading feedback...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="glass">
          <CardHeader className="pb-2">
            <CardDescription>Total Reviews</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{stats?.total_feedback || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="glass">
          <CardHeader className="pb-2">
            <CardDescription>Average Rating</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <p className="text-3xl font-bold">{(stats?.average_rating || 0).toFixed(1)}</p>
              <Star className="w-6 h-6 fill-amber-400 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="glass bg-green-50/50">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <TrendingUp className="w-4 h-4 text-green-600" /> Positive
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-green-600">{stats?.positive_count || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="glass bg-red-50/50">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <TrendingDown className="w-4 h-4 text-red-600" /> Needs Attention
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-red-600">{stats?.negative_count || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b pb-2">
        <Button 
          variant={activeTab === 'received' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('received')}
        >
          <MessageSquare className="w-4 h-4 mr-2" />
          Feedback Received ({feedbackReceived.length})
        </Button>
        <Button 
          variant={activeTab === 'given' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('given')}
        >
          <User className="w-4 h-4 mr-2" />
          Customer Ratings ({customerFeedback.length})
        </Button>
      </div>

      {/* Received Feedback Tab */}
      {activeTab === 'received' && (
        <div className="space-y-4">
          {/* Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select value={filterRating} onValueChange={setFilterRating}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Filter by rating" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Feedback</SelectItem>
                <SelectItem value="positive">Positive (4-5★)</SelectItem>
                <SelectItem value="neutral">Neutral (3★)</SelectItem>
                <SelectItem value="negative">Negative (1-2★)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {filteredFeedback.length === 0 ? (
            <div className="text-center py-12 glass rounded-xl">
              <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No feedback received yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Customer feedback will appear here after services
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredFeedback.map((feedback, idx) => (
                <div 
                  key={idx} 
                  className={`glass rounded-xl p-4 border-l-4 ${
                    getSentiment(feedback.overall_experience) === 'positive' ? 'border-green-500' :
                    getSentiment(feedback.overall_experience) === 'negative' ? 'border-red-500' :
                    'border-gray-400'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium">{feedback.customer_name}</p>
                        <SentimentBadge sentiment={getSentiment(feedback.overall_experience)} />
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="w-3 h-3" />
                        {feedback.date}
                        <span>•</span>
                        {feedback.service_type}
                      </div>
                    </div>
                    <div className="text-right">
                      <StarDisplay rating={feedback.overall_experience} />
                      <p className="text-xs text-muted-foreground mt-1">Overall</p>
                    </div>
                  </div>
                  
                  {/* Expandable Details */}
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="mt-2 text-xs"
                    onClick={() => setExpandedFeedback(expandedFeedback === idx ? null : idx)}
                  >
                    {expandedFeedback === idx ? (
                      <><ChevronUp className="w-3 h-3 mr-1" /> Hide Details</>
                    ) : (
                      <><ChevronDown className="w-3 h-3 mr-1" /> View Details</>
                    )}
                  </Button>
                  
                  {expandedFeedback === idx && (
                    <div className="mt-3 pt-3 border-t space-y-3">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div>
                          <p className="text-muted-foreground">Skill</p>
                          <StarDisplay rating={feedback.therapist_skill} size="sm" />
                        </div>
                        <div>
                          <p className="text-muted-foreground">Professionalism</p>
                          <StarDisplay rating={feedback.therapist_professionalism} size="sm" />
                        </div>
                        <div>
                          <p className="text-muted-foreground">Communication</p>
                          <StarDisplay rating={feedback.therapist_communication} size="sm" />
                        </div>
                        <div>
                          <p className="text-muted-foreground">Punctuality</p>
                          <StarDisplay rating={feedback.therapist_punctuality} size="sm" />
                        </div>
                      </div>
                      
                      {feedback.what_you_liked && (
                        <div className="bg-green-50 p-3 rounded-lg">
                          <p className="text-xs text-green-700 font-medium mb-1">What they liked:</p>
                          <p className="text-sm text-green-800">{feedback.what_you_liked}</p>
                        </div>
                      )}
                      
                      {feedback.areas_of_improvement && (
                        <div className="bg-amber-50 p-3 rounded-lg">
                          <p className="text-xs text-amber-700 font-medium mb-1">Suggested improvements:</p>
                          <p className="text-sm text-amber-800">{feedback.areas_of_improvement}</p>
                        </div>
                      )}
                      
                      {feedback.additional_comments && (
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-xs text-gray-600 font-medium mb-1">Additional comments:</p>
                          <p className="text-sm">{feedback.additional_comments}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Customer Ratings Tab */}
      {activeTab === 'given' && (
        <div className="space-y-4">
          <div className="glass rounded-xl p-4 bg-blue-50/50">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-800">Rate Your Customers</p>
                <p className="text-sm text-blue-600">
                  Your feedback helps us identify good customers and flag problematic ones. 
                  This is confidential and used for internal analysis.
                </p>
              </div>
            </div>
          </div>

          {customerFeedback.length === 0 ? (
            <div className="text-center py-12 glass rounded-xl">
              <User className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No customer ratings given yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                After completing a service, you can rate the customer
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {customerFeedback.map((cf, idx) => (
                <div key={idx} className="glass rounded-xl p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{cf.customer_name}</p>
                      <p className="text-sm text-muted-foreground">{cf.customer_phone}</p>
                    </div>
                    <Badge variant={cf.would_serve_again === 'yes' ? 'default' : 'destructive'}>
                      {cf.would_serve_again === 'yes' ? 
                        <><ThumbsUp className="w-3 h-3 mr-1" /> Would serve again</> :
                        <><ThumbsDown className="w-3 h-3 mr-1" /> Flagged</>
                      }
                    </Badge>
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">Behavior:</span> {cf.behavior}
                    </div>
                    <div>
                      <span className="text-muted-foreground">Punctuality:</span> {cf.punctuality}
                    </div>
                    <div>
                      <span className="text-muted-foreground">Communication:</span> {cf.communication}
                    </div>
                  </div>
                  {cf.notes && (
                    <p className="mt-2 text-sm text-muted-foreground italic">"{cf.notes}"</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Customer Feedback Dialog */}
      <Dialog open={feedbackDialogOpen} onOpenChange={setFeedbackDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif">Rate Customer</DialogTitle>
          </DialogHeader>
          
          {selectedCustomer && (
            <div className="space-y-4">
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="font-medium">{selectedCustomer.customer_name}</p>
                <p className="text-sm text-muted-foreground">{selectedCustomer.customer_phone}</p>
              </div>
              
              <div className="space-y-3">
                <div>
                  <Label>Behavior *</Label>
                  <RadioGroup 
                    value={customerRating.behavior} 
                    onValueChange={(v) => setCustomerRating(prev => ({...prev, behavior: v}))}
                    className="flex gap-4 mt-1"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="excellent" id="beh-excellent" />
                      <Label htmlFor="beh-excellent">Excellent</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="good" id="beh-good" />
                      <Label htmlFor="beh-good">Good</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="problematic" id="beh-prob" />
                      <Label htmlFor="beh-prob">Problematic</Label>
                    </div>
                  </RadioGroup>
                </div>
                
                <div>
                  <Label>Punctuality</Label>
                  <RadioGroup 
                    value={customerRating.punctuality} 
                    onValueChange={(v) => setCustomerRating(prev => ({...prev, punctuality: v}))}
                    className="flex gap-4 mt-1"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="on_time" id="punct-on" />
                      <Label htmlFor="punct-on">On Time</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="late" id="punct-late" />
                      <Label htmlFor="punct-late">Late</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="no_show" id="punct-no" />
                      <Label htmlFor="punct-no">No Show</Label>
                    </div>
                  </RadioGroup>
                </div>
                
                <div>
                  <Label>Communication</Label>
                  <RadioGroup 
                    value={customerRating.communication} 
                    onValueChange={(v) => setCustomerRating(prev => ({...prev, communication: v}))}
                    className="flex gap-4 mt-1"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="clear" id="comm-clear" />
                      <Label htmlFor="comm-clear">Clear</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="unclear" id="comm-unclear" />
                      <Label htmlFor="comm-unclear">Unclear</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="difficult" id="comm-diff" />
                      <Label htmlFor="comm-diff">Difficult</Label>
                    </div>
                  </RadioGroup>
                </div>
                
                <div>
                  <Label>Would you serve this customer again? *</Label>
                  <RadioGroup 
                    value={customerRating.would_serve_again} 
                    onValueChange={(v) => setCustomerRating(prev => ({...prev, would_serve_again: v}))}
                    className="flex gap-4 mt-1"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="yes" id="serve-yes" />
                      <Label htmlFor="serve-yes" className="flex items-center gap-1">
                        <ThumbsUp className="w-4 h-4 text-green-600" /> Yes
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="no" id="serve-no" />
                      <Label htmlFor="serve-no" className="flex items-center gap-1">
                        <ThumbsDown className="w-4 h-4 text-red-600" /> No
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
                
                <div>
                  <Label>Notes (Optional)</Label>
                  <Textarea
                    value={customerRating.notes}
                    onChange={(e) => setCustomerRating(prev => ({...prev, notes: e.target.value}))}
                    placeholder="Any specific observations about this customer..."
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setFeedbackDialogOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={submitCustomerFeedback} disabled={submittingFeedback} className="flex-1">
                  {submittingFeedback ? 'Submitting...' : 'Submit'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TherapistFeedback;
